import { Order } from "../../Models/Order";
import { Cache } from "./Cache/Cache";
import { DateTimeProvider } from "../DateTimeProvider";
import { INotifier } from "../Notifier/INotifier";
import { Throttle } from "../../Utils/Throttle";
import { TicketsRepo } from "./TicketsRepo";
import { Datasets } from "@tblabs/storage";
import { OrdersFolders } from "../../Models/OrdersFolders";
import { v4 as uuidv4 } from 'uuid';
import { Collection } from "@tblabs/truffle";

export class OrderUpdateResult
{
    public NotificationHasBeenSent: boolean = false;
}

export class OrdersManager
{
    private cache = new Cache<Order>();

    private Log(message: string)
    {
        // console.log(message);
    }

    public get Orders(): Collection<Order>
    {
        return this.ordersCollection;
    }

    constructor(
        private _orders: Datasets<Order>,
        private _tickets: TicketsRepo,
        private _notifier: INotifier)
    { }

    public async OpenTicket(ticketId: string): Promise<Order | null>
    {
        const order = await this._tickets.Open(ticketId);

        if (!order)
        {
            return null;
        }

        order.SelfSetup();

        await this.Add(order);

        await this._tickets.Close(ticketId);

        return order;
    }

    public async Add(order: Order): Promise<void>
    {
        await this._orders.Add(OrdersFolders.Open, order.Id, order);

        this.cache.UpdateOrAdd(order);
    }

    private async GetOneFromRepo(orderId: string): Promise<Order | null>
    {
        try
        {
            const order = await this._orders.GetOne(OrdersFolders.Open, orderId);
        
            order.Meta.Folder = OrdersFolders.Open;

            return order;
        }
        catch (error)
        {
            try 
            {
                const order = await this._orders.GetOne(OrdersFolders.Closed, orderId);
        
                order.Meta.Folder = OrdersFolders.Closed;
    
                return order;
            }
            catch (error)
            {
                return null;
            }
        }   
    }

    public async GetOne(orderId: string): Promise<Order>
    {
        // 1. Try from cache
        let orderFromCache: Order | null = this.cache.FindOne(orderId);

        if (orderFromCache)
        {
            this.Log('Found in cache');

            return orderFromCache;
        }

        // 2. Try from remote repo
        const orderFromRepo: Order | null = await this.GetOneFromRepo(orderId);

        if (orderFromRepo)
        {
            this.Log('Found in repo');

            this.cache.UpdateOrAdd(orderFromRepo);

            return orderFromRepo;
        }

        // 3. Check tickets
        const orderFromTicket: Order | null = await this.OpenTicket(orderId);

        if (orderFromTicket)
        {
            this.Log('Found in tickets');

            this.cache.UpdateOrAdd(orderFromTicket);

            return orderFromTicket;
        }

        throw new Error("Order not found");
    }

    public async FetchAll(folder: OrdersFolders = OrdersFolders.Open): Promise<Order[]>
    {
        // 1. Get all orders from repo
        const orders: Order[] = await this._orders.GetAll(folder);

        this.cache.UpdateOrAdd(...orders);

        // 2. Get all orders from tickets only if requesting current orders
        if (folder === OrdersFolders.Open)
        {
            const ordersFromTickets: Order[] = await this._tickets.OpenAll();
      
            this.cache.UpdateOrAdd(...ordersFromTickets);
            
            orders.push(...ordersFromTickets);
        }

        orders.forEach(x => x.Meta.Folder = folder);

        return orders;
    }

    private ordersCollection = new Collection<Order>();
    public async LoadAll(folder: OrdersFolders = OrdersFolders.Open): Promise<void>
    {
        const orders: Order[] = await this.FetchAll(folder);

        orders.forEach(x => x.SelfSetup());
        orders.sort((a, b) => +b.Meta.CreatedAt - +a.Meta.CreatedAt);
        this.ordersCollection.Load(...orders);
    }

    public async Update(order: Order): Promise<OrderUpdateResult>
    {
        const result = new OrderUpdateResult();

        const canNotify = Throttle.Throttle(order.Meta.UpdatedAt, 10 * 60);

        order.Meta.UpdatedAt = DateTimeProvider.Now;
        order.Meta.UpdatedBy = this._orders.Database.User.Id || "(unknown user id)";

        await this._orders.Update(order.Meta.Folder, order.Id, order);

        this.cache.UpdateOrAdd(order);

        if (canNotify)
        {
            await this._notifier.NotifyOrderUpdate(order);
            result.NotificationHasBeenSent = true;
        }

        return result;
    }

    public async Close(order: Order): Promise<void>
    {
        order.Meta.Folder = OrdersFolders.Closed;
        order.Meta.UpdatedAt = DateTimeProvider.Now;

        const currentFolder = OrdersFolders.Open;

        await this._orders.Update(currentFolder, order.Id, order);
        await this._orders.Move(order.Id, currentFolder, OrdersFolders.Closed);
    }
    
    public async Reopen(order: Order): Promise<void>
    {
        order.Meta.Folder = OrdersFolders.Open;
        order.Meta.UpdatedAt = DateTimeProvider.Now;

        const currentFolder = OrdersFolders.Closed;

        await this._orders.Update(currentFolder, order.Id, order);
        await this._orders.Move(order.Id, currentFolder, OrdersFolders.Open);
    }

    public async Delete(order: Order): Promise<void>
    {
        try
        {
            this.Log(`Deleting order ${order.Id} in "${order.Meta.Folder}"...`);

            await this._orders.Delete(order.Meta.Folder, order.Id);
            this.cache.Remove(order);
        } 
        catch (error: any)
        {
            try
            {
                this.Log(`Error deleting order ${order.Id}: ${error.message}. Trying in tickets...`);
                
                await this._tickets.Close(order.Id);
                this.cache.Remove(order);
            } 
            catch (error: any)
            {
                // await this._orders.Delete(OrdersFolders.Closed, order.Id);
                this.Log(`Error deleting ticket ${order.Id}: ${error.message}`);
            }
        }
    }

    public async Duplicate(order: Order): Promise<Order>
    {
        const newOrder = new Order();

        newOrder.Id = uuidv4().toUpperCase();
        newOrder.Meta.CreatedAt = DateTimeProvider.Now;
        newOrder.Meta.CreatedBy = this._orders.Database.User?.Id || "(unknown user id)";

        const steps = [...order.Steps.Items];
        steps.forEach(x => x.ResetState());
        newOrder.Steps.Load(...steps);

        newOrder.People.Load(...order.People.Items);

        await this.Add(newOrder);

        return newOrder;
        // throw new Error("z dowolnego folderu do Open! + zmiana detali orderu");
    }
}
